import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import { connect } from "react-redux";
import { Dispatch } from 'redux';

import { BookemZulu as Model } from 'src/core/services/data/JayDataContext';
import { FormatService } from 'src/core/services/format.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ProductContractsView } from './product-contracts.view';
import { ContractDataService } from '../../../contract/form/contract-form.data';
import { ContractRangeDateListDataService } from 'src/app/products/contract/form/contract-range-date.data';
import { ContractServiceTypeListDataService } from 'src/app/products/contract/form/contract-service-type-list.data';
import { ContractMarketDataService } from 'src/app/products/contract/form/contract-market-form.data';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	contracts: ContractItem[];
}

interface PropsFromDispatch {
	navigateToContract: (
		contract: ContractItem,
		title: string,
		text: string,
		key: string,
		product?: ProductItem,
		defaultLanguage?: string,
	) => any;
	contractsLoaded: (product: ProductItem | TourItineraryItem, contracts: ContractItem[]) => any;
	datesLoaded: (contract: ContractItem, dates: ContractRangeDateItem[]) => any;
	marketsLoaded: (contractItem: ContractItem, markets: ContractMarketItem[]) => any;
	servicesLoaded: (contractItem: ContractItem, markets: ContractServiceTypeItem[]) => any;
}

interface Props extends ComponentViewProps<ProductItem | TourItineraryItem> {
	mode: ControlMode;
	contextService: any;
	defaultLanguage: string;
}

@observer
class ProductContractsController extends React.Component<Props & PropsFromDispatch & PropsFromState, {}> {
	@observable
	protected dataService: ContractDataService;

	protected contractDataService: ContractDataService;

	@resolve
	protected toast: ToastNotifications;

	@resolve
	protected formatService: FormatService;

	componentDidMount() {
		this.contractDataService = ContractDataService.make(this.props.contextService);
		this.loadData();
	}

	@autobind
	cloneDateBandsContract(origenContract: ContractItem, contract: ContractItem, year: number): Promise<ContractItem> {
		const contractId = origenContract.id;
		let origenDateBands = ObjectUtils.deepCopy(origenContract.dateBands);
		return new Promise<ContractItem>((resolve, reject) => {
			if (contractId > 0) {
				const dataServiceDB: ContractRangeDateListDataService = ContractRangeDateListDataService.make(this.props.contextService, this.formatService);
				dataServiceDB.addFilter('contractId', 'eq', contractId);
				dataServiceDB.addSorting('bookingDateFrom', 'asc', true);
				dataServiceDB.pageSize = 20;
				dataServiceDB.load(query => query)
					.then(dateBands => {
						Promise.all(dateBands.map(db => dataServiceDB.create(() => dataServiceDB.clone(db))))
							.then(res => {
								for (let date of res) {
									date.contractId = contract.id;
								}
								this.UpdateYearDateBand(res, year);
								this.props.datesLoaded(contract, res.slice());
								resolve(contract);
							});
						if (dateBands == null || dateBands.length == 0)
							resolve(contract);
					})
					.catch(reject)
			} else {
				if (origenDateBands && origenDateBands.length > 0)
					for (let date of origenDateBands) {
						date.contractId = contract.id;
					}
				this.UpdateYearDateBand(origenDateBands, year);
				this.props.datesLoaded(contract, origenDateBands);
				resolve(contract);
			}
		});
	}

	@autobind
	cloneServicesContract(origenContract: ContractItem, contract: ContractItem): Promise<ContractItem> {
		const contractId = origenContract.id;
		let origenServices = ObjectUtils.deepCopy(origenContract.services);
		return new Promise<ContractItem>((resolve, reject) => {
			if (contractId > 0) {
				const dataS: ContractServiceTypeListDataService = ContractServiceTypeListDataService.make(this.props.contextService);
				dataS.addFilter('contractId', 'eq', contractId);
				dataS.addSorting('id', 'asc', true);
				dataS.pageSize = 20;
				dataS.load(query => query)
					.then(services => {
						Promise.all(services.map(s => dataS.create(() => dataS.clone(s))))
							.then(res => {
								for (let service of res) {
									service.contractId = contract.id;
								}
								this.props.servicesLoaded(contract, res.slice());
								resolve(contract);
							});
						if (services == null || services.length == 0)
							resolve(contract);
					})
					.catch(reject)
			} else {
				for (let service of origenServices) {
					service.contractId = contract.id;
				}
				this.props.servicesLoaded(contract, origenServices.slice());
				resolve(contract);
			}
		});
	}

	@autobind
	cloneMarketsContract(origenContract: ContractItem, contract: ContractItem): Promise<ContractItem> {
		const contractId = origenContract.id;
		let origenMarkets = ObjectUtils.deepCopy(origenContract.markets);
		return new Promise<ContractItem>((resolve, reject) => {
			if (contractId > 0) {
				const dataServiceM: ContractMarketDataService = ContractMarketDataService.make(this.props.contextService);
				dataServiceM.addFilter('contractId', 'eq', contractId);
				dataServiceM.addSorting('id', 'asc', true);
				dataServiceM.pageSize = 20;
				dataServiceM.load(query => query)
					.then((markets) => {
						Promise.all(markets.map(m => dataServiceM.create(() => dataServiceM.clone(m))))
							.then((res) => {
								for (let market of res) {
									market.contractId = contract.id;
								}
								this.props.marketsLoaded(contract, res.slice());
								resolve(contract);
							});

						if (markets == null || markets.length == 0) {
							resolve(contract);
						}
					})
					.catch(reject)
			} else {
				for (let market of origenMarkets) {
					market.contractId = contract.id;
				}
				this.props.marketsLoaded(contract, origenMarkets.slice());
				resolve(contract);
			}
		});
	}

	@autobind
	addYear(dateFrom: any, dateTo: any, diff: number) {
		let yearDateFrom = dateFrom.year() + diff;
		let yearDateTo = yearDateFrom + (dateTo.year() - dateFrom.year());
		dateFrom.year(yearDateFrom);
		dateTo.year(yearDateTo);
	}
	@autobind
	UpdateYearDateBand(dateBands: ContractRangeDateItem[], year: number) {
		if (dateBands) {
			let dateBPreviusBeforeChange = null
			let dateSPreviuBeforeChange = null
			let dateBPreviusAfterChange = null
			let dateSPreviuAfterChange = null
			for (let index = 0; index < dateBands.length; index++) {
				let date = dateBands[index];
				let bookingDateFrom = moment.utc(date.bookingDateFrom).startOf('day')
				let bookingDateTo = moment.utc(date.bookingDateTo).startOf('day')
				let serviceDateFrom = moment.utc(date.serviceDateFrom).startOf('day')
				let serviceDateTo = moment.utc(date.serviceDateTo).startOf('day')
				let diffB = bookingDateTo.diff(bookingDateFrom, "year");
				let diffS = serviceDateTo.diff(serviceDateFrom, "year");
				if (index == 0) {
					bookingDateFrom = bookingDateFrom.year(year);
					serviceDateFrom = serviceDateFrom.year(year);
				}
				else {
					let diffBefore = bookingDateFrom.diff(dateBPreviusBeforeChange, "year");
					bookingDateFrom = bookingDateFrom.year(dateBPreviusAfterChange.year() + diffBefore);
					diffBefore = serviceDateFrom.diff(dateSPreviuBeforeChange, "year");
					serviceDateFrom = serviceDateFrom.year(dateSPreviuAfterChange.year() + diffBefore);
				}
				dateBPreviusBeforeChange = moment.utc(date.bookingDateTo).startOf('day')
				dateSPreviuBeforeChange = moment.utc(date.serviceDateTo).startOf('day')

				bookingDateTo = bookingDateTo.year(bookingDateFrom.year() + diffB);
				serviceDateTo = serviceDateTo.year(serviceDateFrom.year() + diffS);

				dateBPreviusAfterChange = bookingDateTo;
				dateSPreviuAfterChange = serviceDateTo;

				date.bookingDateFrom = bookingDateFrom.toDate();
				date.bookingDateTo = bookingDateTo.toDate();
				date.serviceDateFrom = serviceDateFrom.toDate();
				date.serviceDateTo = serviceDateTo.toDate();
			}
		}
	}
	@autobind
	UpdateYearContract(contract: ContractItem, year: number) {

		const cost: CostItem = contract.dynamicProperties['cost'];
		const priceItem: PriceItem = contract.dynamicProperties['price'];
		if (cost && cost.elements) {
			let dateBPreviusBeforeChange = null
			let dateSPreviuBeforeChange = null
			let dateBPreviusAfterChange = null
			let dateSPreviuAfterChange = null
			for (let index = 0; index < cost.elements.length; index++) {
				let price = cost.elements[index];
				let bookingDateFrom = moment.utc(price.bookingDateFrom).startOf('day')
				let bookingDateTo = moment.utc(price.bookingDateTo).startOf('day')
				let serviceDateFrom = moment.utc(price.serviceDateFrom).startOf('day')
				let serviceDateTo = moment.utc(price.serviceDateTo).startOf('day')
				let diffB = bookingDateTo.diff(bookingDateFrom, "year");
				let diffS = serviceDateTo.diff(serviceDateFrom, "year");
				if (index == 0) {
					bookingDateFrom = bookingDateFrom.year(year);
					serviceDateFrom = serviceDateFrom.year(year);
				}
				else {
					let diffBefore = bookingDateFrom.diff(dateBPreviusBeforeChange, "year");
					bookingDateFrom = bookingDateFrom.year(dateBPreviusAfterChange.year() + diffBefore);
					diffBefore = serviceDateFrom.diff(dateSPreviuBeforeChange, "year");
					serviceDateFrom = serviceDateFrom.year(dateSPreviuAfterChange.year() + diffBefore);
				}
				dateBPreviusBeforeChange = moment.utc(price.bookingDateTo).startOf('day')
				dateSPreviuBeforeChange = moment.utc(price.serviceDateTo).startOf('day')

				bookingDateTo = bookingDateTo.year(bookingDateFrom.year() + diffB);
				serviceDateTo = serviceDateTo.year(serviceDateFrom.year() + diffS);

				dateBPreviusAfterChange = bookingDateTo;
				dateSPreviuAfterChange = serviceDateTo;

				price.bookingDateFrom = bookingDateFrom.toDate();
				price.bookingDateTo = bookingDateTo.toDate();
				price.serviceDateFrom = serviceDateFrom.toDate();
				price.serviceDateTo = serviceDateTo.toDate();
				price.bookingDateFromText = this.formatService.toString('short-date', price.bookingDateFrom, "utc");
				price.bookingDateToText = this.formatService.toString('short-date', price.bookingDateTo, "utc");
				price.serviceDateFromText = this.formatService.toString('short-date', price.serviceDateFrom, "utc");
				price.serviceDateToText = this.formatService.toString('short-date', price.serviceDateTo, "utc");
			}


		}
		if (priceItem && priceItem.elements) {
			let dateBPreviusBeforeChange = null
			let dateSPreviuBeforeChange = null
			let dateBPreviusAfterChange = null
			let dateSPreviuAfterChange = null
			//let array = priceItem.elements.sort((x, y) => x.bookingDateFrom <= y.bookingDateFrom ? -1 : 1);
			for (let index = 0; index < priceItem.elements.length; index++) {
				let price = priceItem.elements[index];
				let bookingDateFrom = moment.utc(price.bookingDateFrom).startOf('day')
				let bookingDateTo = moment.utc(price.bookingDateTo).startOf('day')
				let serviceDateFrom = moment.utc(price.serviceDateFrom).startOf('day')
				let serviceDateTo = moment.utc(price.serviceDateTo).startOf('day')
				let diffB = bookingDateTo.diff(bookingDateFrom, "year");
				let diffS = serviceDateTo.diff(serviceDateFrom, "year");
				if (index == 0) {
					bookingDateFrom = bookingDateFrom.year(year);
					serviceDateFrom = serviceDateFrom.year(year);
				}
				else {
					let diffBefore = bookingDateFrom.diff(dateBPreviusBeforeChange, "year");
					bookingDateFrom = bookingDateFrom.year(dateBPreviusAfterChange.year() + diffBefore);
					diffBefore = serviceDateFrom.diff(dateSPreviuBeforeChange, "year");
					serviceDateFrom = serviceDateFrom.year(dateSPreviuAfterChange.year() + diffBefore);
				}
				dateBPreviusBeforeChange = moment.utc(price.bookingDateTo).startOf('day')
				dateSPreviuBeforeChange = moment.utc(price.serviceDateTo).startOf('day')

				bookingDateTo = bookingDateTo.year(bookingDateFrom.year() + diffB);
				serviceDateTo = serviceDateTo.year(serviceDateFrom.year() + diffS);

				dateBPreviusAfterChange = bookingDateTo;
				dateSPreviuAfterChange = serviceDateTo;

				price.bookingDateFrom = bookingDateFrom.toDate();
				price.bookingDateTo = bookingDateTo.toDate();
				price.serviceDateFrom = serviceDateFrom.toDate();
				price.serviceDateTo = serviceDateTo.toDate();
				price.bookingDateFromText = this.formatService.toString('short-date', price.bookingDateFrom, "utc");
				price.bookingDateToText = this.formatService.toString('short-date', price.bookingDateTo, "utc");
				price.serviceDateFromText = this.formatService.toString('short-date', price.serviceDateFrom, "utc");
				price.serviceDateToText = this.formatService.toString('short-date', price.serviceDateTo, "utc");
			}
		}
	}

	@autobind
	clone(contract: ContractItem, year: number, product: ProductItem | TourItineraryItem) {
		this.dataService.create(() => (this.dataService as ContractDataService).clone(contract))
			.then(res => {
				if (product.entity.getType() == Model.Products.Logic.ViewModels.ProductItem) {
					res.productId = product.id;
				} else {
					res.tourItineraryId = product.id;
				}
				this.UpdateYearContract(res, year);
				this.props.contractsLoaded(this.props.model, [res]);
				Promise.all([
					this.cloneDateBandsContract(contract, res, year),
					this.cloneMarketsContract(contract, res),
					this.cloneServicesContract(contract, res)]).then(result => {
						let contractResult = result[result.length - 1];
						this.onItemDoubleClicked(contractResult);
					});
			});
	}

	@autobind
	cloneAccept(year: number, product: ProductItem | TourItineraryItem, contractClone?: ContractItem, contractCloneId?: number) {
		this.contractDataService.addFilter('id', 'eq', contractCloneId);
		this.contractDataService.addSorting('id', 'asc', true);
		this.contractDataService.pageSize = 1;
		this.contractDataService.load(query => query)
			.then((items) => {
				contractClone = items[0];
				this.clone(contractClone, year, product);
			})
			.catch((error) => {
				console.log(error);
			});
	}

	@autobind
	protected async onItemDoubleClicked(item: ContractItem): Promise<void> {
		const { model, defaultLanguage, navigateToContract, contractsLoaded } = this.props;
		const errors = await model.validate(false);
		if (errors.length != 0) {
			this.toast.showToast('Validation error!', errors.map(o => o.text), 'error');
			return;
		}

		if (item == null) {
			item = await this.dataService.add(model, defaultLanguage);
			contractsLoaded(model, [item]);
		}
		const text = item.dynamicProperties['displayName'][defaultLanguage] || 'New';
		if (this.props.model.entity.getType() == Model.Products.Logic.ViewModels.ProductItem) {
			navigateToContract(item, 'Contract', text, 'contract', model as ProductItem, defaultLanguage);
		} else {
			navigateToContract(item, 'Contract', text, 'contract');
		}
	}

	@autobind
	protected async loadData(): Promise<void> {
		const { model, contracts, setBusy, contractsLoaded, contextService } = this.props;

		this.dataService = ContractDataService.make(contextService);

		if (contracts == null && model.id) {
			this.dataService.addFilter('isDeleted', 'eq', false);
			let prop: keyof ContractItem;
			if (this.props.model.entity.getType() == Model.Products.Logic.ViewModels.ProductItem) {
				prop = "productId";
			} else {
				prop = "tourItineraryId";
			}
			this.dataService.addFilter(prop, 'eq', model.id);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 25;
			try {
				setBusy(true);
				const items = await this.dataService.load(query => query);
				contractsLoaded(model, items.slice());
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				setBusy(false);
			}
		} else {
			this.dataService.items = contracts;
		}
	}

	render() {
		if (this.props.model == null || this.dataService == null) {
			return null;
		}

		const model = this.props.model;
		return (
			<ProductContractsView
				mode={this.props.mode}
				cloneAccept={this.cloneAccept}
				model={model}
				contextService={this.props.contextService}
				setBusy={this.props.setBusy}
				dataService={this.dataService}
				onItemDoubleClicked={this.onItemDoubleClicked}
			/>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: ComponentViewProps<ProductItem | TourItineraryItem>
): PropsFromState => ({
	contracts: (state.otherLoadedEntities.find(e => e.entity == props.model.entity) as ProductItem).contracts,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	navigateToContract: (
		entity,
		title: string,
		text: string,
		key: string,
		product?: ProductItem,
		defaultLanguage?: string,
	) => dispatch(actionCreators.productFormUpNav(entity, title, text, key, product
		? {
			entity: product,
			key: "product",
			title: "Product",
			text: product.dynamicProperties['displayName'][defaultLanguage],
		}
		: undefined)),
	contractsLoaded: (
		product: ProductItem | TourItineraryItem,
		contracts: ContractItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(product, contracts, "contracts")),
	datesLoaded: (
		contract: ContractItem,
		dates: ContractRangeDateItem[]
	) => dispatch(actionCreators.productFormRelationshipLoaded(contract, dates, "dateBands")),
	marketsLoaded: (
		contract: ContractItem,
		markets: ContractMarketItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(contract, markets, "markets")),
	servicesLoaded: (
		contract: ContractItem,
		services: ContractServiceTypeItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(contract, services, "services")),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(ProductContractsController);
export { connected as ProductContractsController };



// WEBPACK FOOTER //
// ./src/app/products/products/form/parts/product-contracts.controller.tsx